import { Alert, Box } from '@mui/material';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import { UPDropdownItem } from 'components/dropdown';
import { CompanyRole } from 'constants/CompanyRole';
import { useGridPagination } from 'hooks/useGridPagination';
import { useGridSearch } from 'hooks/useGridSearch';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getEmployeeFunctionLinksAction } from 'store/actions/employee-function-link-actions';
import { getActiveEmployeeDropdownItems } from 'store/actions/employee-actions';
import { functionsSelector } from 'store/selectors/company-functions-selectors';
import {
  deleteEmployeeFunctionLinkErrorSelector,
  employeeFunctionLinksSelector,
} from 'store/selectors/employee-function-link-selectors';
import { activeEmployeeDropdownSelector } from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  EMPLOYEE_FUNCTION_LINKS__ADD_BUTTON,
  EMPLOYEE_FUNCTION_LINKS__ALL_EMPLOYEES_LINKED,
  EMPLOYEE_FUNCTION_LINKS__NO_EMPLOYEES_FOUND,
  EMPLOYEE_FUNCTION_LINKS__SECTION_TITLE,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import IEmployeeFunctionLink from 'types/company/payroll/IEmployeeFunctionLink';
import EmployeeFunctionLinkModal from './EmployeeFunctionLinkModal';
import EmployeeFunctionLinksGrid from './EmployeeFunctionLinksGrid';
import { buildLinkRows } from './helpers';
import { AddEmployeeFunctionLinkButton, SectionContainer, SectionTitle } from './styles';
import { IEmployeeFunctionLinkRow } from './types';

const MANAGE_ROLES = [CompanyRole.ADMIN, CompanyRole.COMPANY_MANAGER];

const EmployeeFunctionLinks = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const links = useAppSelector(employeeFunctionLinksSelector);
  const employees = useAppSelector(activeEmployeeDropdownSelector);
  const functions = useAppSelector(functionsSelector);
  const deleteError = useAppSelector(deleteEmployeeFunctionLinkErrorSelector);
  const canManage = MANAGE_ROLES.includes(useAppSelector(profileSelector).role);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [linkToEdit, setLinkToEdit] = useState<IEmployeeFunctionLink | undefined>(undefined);

  useEffect(() => {
    if (companyId) {
      dispatch(getEmployeeFunctionLinksAction(toNumber(companyId)));
      dispatch(getActiveEmployeeDropdownItems({ companyId: toNumber(companyId) }));
    }
  }, [companyId, dispatch]);

  const rows = useMemo(() => buildLinkRows(links, employees, functions ?? []), [links, employees, functions]);

  const matches = useCallback((row: IEmployeeFunctionLinkRow, term: string) => {
    const needle = term.toLowerCase();
    return row.employeeName.toLowerCase().includes(needle) || row.functionsLabel.toLowerCase().includes(needle);
  }, []);

  const { searchTerm, onSearch, filteredRows } = useGridSearch(rows, matches);
  const getRowId = useCallback((row: IEmployeeFunctionLinkRow) => row.employeeId, []);
  const { resetToFirstPage } = useGridPagination(filteredRows, getRowId);

  const onSearchChange = useCallback(
    (value: string) => {
      onSearch(value);
      resetToFirstPage();
    },
    [onSearch, resetToFirstPage],
  );

  const linkedEmployeeIds = useMemo(() => links.map(link => link.employeeId), [links]);

  const selectableEmployeeOptions = useMemo(
    () => employees.filter(employee => !linkedEmployeeIds.includes(toNumber(employee.value))),
    [employees, linkedEmployeeIds],
  );

  const functionOptions = useMemo<UPDropdownItem[]>(
    () => (functions ?? []).map(payrollFunction => ({ value: payrollFunction.id, label: payrollFunction.description })),
    [functions],
  );

  const modalEmployeeOptions = useMemo<UPDropdownItem[]>(
    () =>
      linkToEdit
        ? employees.filter(employee => toNumber(employee.value) === linkToEdit.employeeId)
        : selectableEmployeeOptions,
    [employees, linkToEdit, selectableEmployeeOptions],
  );

  const onAdd = useCallback(() => {
    setLinkToEdit(undefined);
    setOpenModal(true);
  }, []);

  const onEdit = useCallback(
    (row: IEmployeeFunctionLinkRow) => {
      setLinkToEdit(links.find(link => link.employeeId === row.employeeId));
      setOpenModal(true);
    },
    [links],
  );

  const noEmployeesLeft = selectableEmployeeOptions.length === 0;

  return (
    <SectionContainer>
      <SectionTitle>{t(EMPLOYEE_FUNCTION_LINKS__SECTION_TITLE)}</SectionTitle>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        {canManage && (
          <AddEmployeeFunctionLinkButton
            text={`+ ${t(EMPLOYEE_FUNCTION_LINKS__ADD_BUTTON)}`}
            onClick={onAdd}
            disabled={noEmployeesLeft}
          />
        )}
        <CustomSearchBox onSearch={onSearchChange} />
      </Box>

      {canManage && noEmployeesLeft && (
        <Box sx={{ marginBottom: '16px' }}>{t(EMPLOYEE_FUNCTION_LINKS__ALL_EMPLOYEES_LINKED)}</Box>
      )}

      {searchTerm && filteredRows.length === 0 ? (
        <Box>{t(EMPLOYEE_FUNCTION_LINKS__NO_EMPLOYEES_FOUND)}</Box>
      ) : (
        <EmployeeFunctionLinksGrid rows={filteredRows} canManage={canManage} onEdit={onEdit} />
      )}

      {deleteError && (
        <Box sx={{ marginTop: '16px' }}>
          <Alert severity="error" icon={false} variant="filled" sx={{ width: '800px' }}>
            {t(deleteError) || t(GENERAL__ERROR_MESSAGE)}
          </Alert>
        </Box>
      )}

      {openModal && (
        <EmployeeFunctionLinkModal
          onClose={() => setOpenModal(false)}
          linkToEdit={linkToEdit}
          employeeOptions={modalEmployeeOptions}
          functionOptions={functionOptions}
        />
      )}
    </SectionContainer>
  );
};

export default EmployeeFunctionLinks;
